import React, { useEffect, useState } from "react";
import {
  Container,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Card,
} from "react-bootstrap";
import Logo from "components/Logo";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import validator from "validator";
import SweetAlertComp from "components/SweetAlert";
import { useLogin } from "./hooks/useLogin";
import { useForgetPassword } from "./hooks/useForgetPassword";
import { useResendVerification } from "./hooks/useResendVerification";
import "./Login.scss";

function Login() {
  const { errors, isAuthenticated } = useSelector((state) => state.login);
  const [errorData, setErrorData] = useState({
    msgTxt: "",
    msgType: "primary",
  });
  const [resendVerificationDisplay, setResendVerificationDisplay] =
    useState(false);
  const [forgotPassDisplay, setForgotPassDisplay] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { mutate: loginMutation, isPending: isLogging } = useLogin();
  const { mutate: forgetPasswordMutation, isPending: isForgettingPassword } =
    useForgetPassword();
  const {
    mutate: resendVerificationMutation,
    isPending: isResendingVerification,
  } = useResendVerification();

  useEffect(() => {
    setErrorData({
      msgTxt: errors,
      msgType: "danger",
    });
  }, [errors]);

  const handleChange = (event, setValue) => {
    setValue(event.target.value);
  };

  const changeForgotPassDisplay = (value) => setForgotPassDisplay(value);
  const changeResendVerificationDisplay = (value) =>
    setResendVerificationDisplay(value);

  const onConfirmForgetPass = (email) => {
    forgetPasswordMutation(
      { email },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(data.result.message);
          } else {
            toast.error(data.error);
          }
          changeForgotPassDisplay(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to send password reset email");
          changeForgotPassDisplay(false);
        },
      }
    );
  };

  const onConfirmResendVerification = (email) => {
    resendVerificationMutation(
      { email },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(data.result.message);
          } else {
            toast.error(data.error);
          }
          changeResendVerificationDisplay(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to resend verification email");
          changeResendVerificationDisplay(false);
        },
      }
    );
  };

  const hideForgetPass = () => changeForgotPassDisplay(false);
  const hideResendVerification = () => changeResendVerificationDisplay(false);
  const displayResendVerification = () => changeResendVerificationDisplay(true);
  const displayForgetPass = () => changeForgotPassDisplay(true);

  const onSubmit = (e) => {
    e.preventDefault();
    loginMutation({ email, password });
  };

  const forgetPasswordModal = SweetAlertComp.bind(this, {
    children: "Please enter your email address here.",
    title: "Forgot password!",
    placeHolder: "Your email address.",
    autoComplete: "on",
    onConfirm: onConfirmForgetPass,
    onCancel: hideForgetPass,
    inputType: "email",
  });

  const resendVerificationModal = SweetAlertComp.bind(this, {
    children: "Please enter your email address here.",
    title: "Re-send Email Verification!",
    placeHolder: "Your email address.",
    autoComplete: "on",
    onConfirm: onConfirmResendVerification,
    onCancel: hideResendVerification,
    inputType: "email",
  });

  return (
    <div className="login-page">
      {forgotPassDisplay && forgetPasswordModal()}
      {resendVerificationDisplay && resendVerificationModal()}

      <Container fluid className="login-container">
        <Row className="justify-content-center align-items-center min-vh-100 g-0">
          <Col xs={12} sm={10} md={8} lg={5} xl={4} className="px-3 px-md-4">
            <Card className="login-card shadow-lg">
              <Card.Body className="p-4 p-md-5">
                <div className="text-center">
                  <div className="logo-container">
                    <Logo />
                  </div>
                </div>

                <div className="login-header">
                  <h2 className="login-title">Welcome Back</h2>
                  <p className="login-subtitle text-muted">
                    Sign in to your account to continue
                  </p>
                </div>

                {errorData.msgTxt && (
                  <Alert
                    variant={errorData.msgType}
                    className={`login-alert ${
                      errorData.msgTxt ===
                      "This account has been locked. Please contact your subscription admin."
                        ? "alert-locked"
                        : ""
                    }`}
                    dismissible
                    onClose={() =>
                      setErrorData({ msgTxt: "", msgType: "primary" })
                    }
                  >
                    {errorData.msgTxt}
                  </Alert>
                )}

                <Form onSubmit={onSubmit} className="login-form">
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label">
                      Email address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(event) => handleChange(event, setEmail)}
                      className="form-control-custom"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="form-label">Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your password"
                      name="password"
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => handleChange(event, setPassword)}
                      className="form-control-custom"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end align-items-center mb-4 mt-4">
                    <Button
                      variant="link"
                      className="forgot-password-link p-0"
                      onClick={displayForgetPass}
                    >
                      Forgot Password?
                    </Button>
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    className="login-button w-100"
                    disabled={
                      isLogging ||
                      !email ||
                      !password ||
                      !validator.isEmail(email)
                    }
                  >
                    {isLogging ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Logging In...
                      </>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </Form>

                {/* <div className="text-center mt-4">
                  <Button
                    id="reSend"
                    variant="link"
                    className="resend-verification-link"
                    onClick={displayResendVerification}
                  >
                    Re-Send Verification to Email?
                  </Button>
                </div>

                <div className="text-center mt-3">
                  <Button id="signUp" variant="link" className="signup-link">
                    <Link to="/signup">Don't have an account? Sign up here.</Link>
                  </Button>
                </div> */}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Login;
